import { SubmissionReasonForSubmittal } from "generated-graphql/graphql";

/**
 * §1 "Reason for Submittal" on the RCRA 8700-12 form. Each option maps to a
 * RCRAInfo notification source code carried by the myRCRAid filing. Labels mirror
 * the form's wording; the source code is appended so staff familiar with RCRAInfo
 * can map back to the radio they know.
 *
 * Ordered with Source N first — the common case for new/continuing sites — to
 * match how the form and our field-sourcing guidance present the choice.
 */
export const REASON_FOR_SUBMITTAL_OPTIONS: ReadonlyArray<{
  value: SubmissionReasonForSubmittal;
  label: string;
}> = [
  {
    value: SubmissionReasonForSubmittal.OngoingRegulatedActivity,
    label: "On-going regulated activity (includes HSM) — Source N",
  },
  {
    value: SubmissionReasonForSubmittal.ElectronicManifestBrokerOnly,
    label: "Electronic Manifest Broker activities only — Source K",
  },
  {
    value: SubmissionReasonForSubmittal.NoLongerOccurring,
    label: "Regulated activity no longer occurring at the site — Source D",
  },
  {
    value: SubmissionReasonForSubmittal.BiennialReport,
    label: "Component of the Biennial/Annual Report — Source B",
  },
];

/**
 * Selectable §1 reasons for the RCRA 8700-12 Site Identification form. Source B
 * (Component of the Biennial/Annual Report) is excluded — it's the reason a site
 * is identified as part of a Biennial Report filing, not a stand-alone 8700-12
 * Site ID submission, so it isn't a valid choice in this stepper.
 *
 * Note: the full list above still backs `formatReasonForSubmittal`, so any
 * existing submission already set to Source B continues to display correctly.
 */
export const RCRA_8700_12_REASON_FOR_SUBMITTAL_OPTIONS = REASON_FOR_SUBMITTAL_OPTIONS.filter(
  ({ value }) => value !== SubmissionReasonForSubmittal.BiennialReport
);

const REASON_FOR_SUBMITTAL_LABEL: Record<SubmissionReasonForSubmittal, string> = Object.fromEntries(
  REASON_FOR_SUBMITTAL_OPTIONS.map(({ value, label }) => [value, label])
) as Record<SubmissionReasonForSubmittal, string>;

/** Narrows an arbitrary string (e.g. a select value) to a §1 reason. */
export function isReasonForSubmittal(value: string): value is SubmissionReasonForSubmittal {
  return REASON_FOR_SUBMITTAL_OPTIONS.some(({ value: optionValue }) => optionValue === value);
}

/** Human-readable label for a §1 reason, or empty string when unset. */
export function formatReasonForSubmittal(
  reason: SubmissionReasonForSubmittal | null | undefined
): string {
  return reason ? REASON_FOR_SUBMITTAL_LABEL[reason] : "";
}
